// hooks/useObjectifsCached.js - Version avec cache intelligent
import { useCallback, useEffect, useState } from 'react';
import { useCacheManager } from './useCacheManager';

const API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL || 'http://localhost:5003/api';

export const useObjectifsCached = (options = {}) => {
    // Options par défaut
    const {
        cacheDuration = 5 * 60 * 1000, // 5 minutes
        forceRefresh = false,
        autoRefresh = true
    } = options;

    // États
    const [objectifs, setObjectifs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetch, setLastFetch] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);

    // Hook de cache
    const { getCacheKey, setCacheData, getCacheData, invalidateCache } = useCacheManager();

    // Headers pour les requêtes
    const getHeaders = useCallback(() => {
        const token = localStorage.getItem('access_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }, []);

    // Fonction utilitaire pour gérer les erreurs
    const handleError = useCallback((error, customMessage = 'Une erreur est survenue') => {
        const message = error?.message || customMessage;
        setError(error);
        console.error('❌ Erreur objectifs:', message);
        return { success: false, error: message };
    }, []);

    // 📋 RÉCUPÉRER LES OBJECTIFS AVEC CACHE
    const fetchObjectifs = useCallback(async (customOptions = {}, useCache = true) => {
        // Préparer la clé de cache
        const options = { stats: true, ...customOptions };
        const cacheKey = getCacheKey('objectifs', options);

        // Vérifier le cache d'abord (sauf si forceRefresh)
        if (useCache && !forceRefresh) {
            const cachedData = getCacheData(cacheKey);
            if (cachedData) {
                console.log('📦 Objectifs chargés depuis le cache');
                setObjectifs(cachedData);
                setIsFromCache(true);
                setError(null);
                return { success: true, objectifs: cachedData, fromCache: true };
            }
        }

        setLoading(true);
        setError(null);
        setIsFromCache(false);

        try {
            // Construction des paramètres de requête
            const params = new URLSearchParams();
            if (options.category) params.append('category', options.category);
            if (options.status) params.append('status', options.status);
            if (options.priority) params.append('priority', options.priority);
            if (options.stats) params.append('stats', 'true');

            const url = `${API_BASE_URL}/objectifs${params.toString() ? '?' + params.toString() : ''}`;

            const response = await fetch(url, {
                headers: getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}`);
            }

            const data = await response.json();
            const fetchedObjectifs = data.objectifs || [];

            // Sauvegarder dans le cache
            setCacheData(cacheKey, fetchedObjectifs, cacheDuration);
            
            setObjectifs(fetchedObjectifs);
            setLastFetch(Date.now());
            
            console.log(`✅ ${fetchedObjectifs.length} objectifs chargés depuis l'API`);
            return { success: true, objectifs: fetchedObjectifs, fromCache: false };
        } catch (err) {
            return handleError(err, 'Erreur lors de la récupération des objectifs');
        } finally {
            setLoading(false);
        }
    }, [getCacheKey, getCacheData, setCacheData, getHeaders, handleError, forceRefresh, cacheDuration]);

    // 🆕 CRÉER UN OBJECTIF avec mise à jour optimiste et invalidation du cache
    const createObjectif = useCallback(async (objectifData) => {
        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE
        const tempObjectif = {
            id_objectif: `temp-${Date.now()}`,
            title: objectifData.title,
            description: objectifData.description || '',
            category: objectifData.category,
            current_value: parseFloat(objectifData.current_value) || 0,
            target_value: parseFloat(objectifData.target_value),
            value_unit: objectifData.value_unit || '',
            status: 'en_cours',
            isOptimistic: true // Marquer comme optimiste
        };

        // Ajouter immédiatement à l'état local pour feedback instantané
        setObjectifs(prev => [tempObjectif, ...prev]);

        try {
            // Validation côté client
            if (!objectifData.title || !objectifData.target_value || !objectifData.category) {
                throw new Error('Titre, valeur cible et catégorie sont requis');
            }

            // Nettoyage des données
            const cleanData = {
                title: objectifData.title,
                description: objectifData.description || '',
                category: objectifData.category,
                custom_category: objectifData.custom_category || null,
                current_value: parseFloat(objectifData.current_value) || 0,
                target_value: parseFloat(objectifData.target_value),
                value_unit: objectifData.value_unit || '',
                value_type: objectifData.value_type || 'number',
                timeline: parseInt(objectifData.timeline),
                timeline_unit: objectifData.timeline_unit || 'mois',
                confidence: parseInt(objectifData.confidence) || 70,
                priority: objectifData.priority || 'medium',
                progress_bar_color: objectifData.progress_bar_color || '#016666',
                trend: objectifData.trend || 'up',
                ordre_affichage: parseInt(objectifData.ordre_affichage) || 0
            };

            const response = await fetch(`${API_BASE_URL}/objectifs`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(cleanData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }

            const data = await response.json();

            // Invalider le cache des objectifs
            invalidateCache('objectifs');
            console.log('🗑️ Cache objectifs invalidé après création');

            // Remplacer l'objectif optimiste par le vrai
            setObjectifs(prev => prev.map(obj => 
                obj.id_objectif === tempObjectif.id_objectif 
                    ? data.objectif 
                    : obj
            ));

            return { success: true, objectif: data.objectif };
        } catch (err) {
            // En cas d'erreur, retirer l'objectif optimiste
            setObjectifs(prev => prev.filter(obj => obj.id_objectif !== tempObjectif.id_objectif));
            return handleError(err, 'Erreur lors de la création de l\'objectif');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, handleError, invalidateCache]);

    // ✏️ MODIFIER UN OBJECTIF avec mise à jour optimiste
    const updateObjectif = useCallback(async (objectifId, updateData) => {
        if (!objectifId) {
            throw new Error('ID objectif requis');
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE - sauvegarder l'état actuel pour rollback
        const previousObjectifs = [...objectifs];
        setObjectifs(prev =>
            prev.map(obj =>
                obj.id_objectif === objectifId
                    ? { ...obj, ...updateData, isOptimistic: true }
                    : obj
            )
        );

        try {
            // Nettoyage des données
            const cleanData = {};
            const allowedFields = [
                'title', 'description', 'category', 'custom_category',
                'current_value', 'target_value', 'value_unit', 'value_type',
                'timeline', 'timeline_unit', 'confidence', 'priority',
                'status', 'progress_bar_color', 'trend', 'ordre_affichage'
            ];

            for (const [key, value] of Object.entries(updateData)) {
                if (allowedFields.includes(key) && value !== undefined) {
                    if (['current_value', 'target_value'].includes(key)) {
                        cleanData[key] = parseFloat(value);
                    } else if (['timeline', 'confidence', 'ordre_affichage'].includes(key)) {
                        cleanData[key] = parseInt(value);
                    } else {
                        cleanData[key] = value;
                    }
                }
            }

            const response = await fetch(`${API_BASE_URL}/objectifs/${objectifId}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(cleanData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }

            const data = await response.json();

            // Invalider le cache des objectifs
            invalidateCache('objectifs');
            console.log('🗑️ Cache objectifs invalidé après modification');

            // Mettre à jour avec les vraies données du serveur
            setObjectifs(prev =>
                prev.map(obj =>
                    obj.id_objectif === objectifId ? data.objectif : obj
                )
            );

            return { success: true, objectif: data.objectif };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setObjectifs(previousObjectifs);
            return handleError(err, 'Erreur lors de la mise à jour de l\'objectif');
        } finally {
            setLoading(false);
        }
    }, [objectifs, getHeaders, handleError, invalidateCache]);

    // 🗑️ SUPPRIMER UN OBJECTIF avec mise à jour optimiste
    const deleteObjectif = useCallback(async (objectifId) => {
        if (!objectifId) {
            throw new Error('ID objectif requis');
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousObjectifs = [...objectifs];
        
        // Retirer immédiatement de l'affichage
        setObjectifs(prev => prev.filter(obj => obj.id_objectif !== objectifId));

        try {
            const response = await fetch(`${API_BASE_URL}/objectifs/${objectifId}`, {
                method: 'DELETE',
                headers: getHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }

            // Invalider le cache des objectifs
            invalidateCache('objectifs');
            console.log('🗑️ Cache objectifs invalidé après suppression');

            const data = await response.json();
            return { success: true, message: data.message };

        } catch (err) {
            // ROLLBACK en cas d'erreur
            setObjectifs(previousObjectifs);
            return handleError(err, 'Erreur lors de la suppression de l\'objectif');
        } finally {
            setLoading(false);
        }
    }, [objectifs, getHeaders, handleError, invalidateCache]);

    // ⭐ METTRE À JOUR LA PROGRESSION avec mise à jour optimiste
    const updateProgress = useCallback(async (objectifId, currentValue) => {
        if (!objectifId) {
            throw new Error('ID objectif requis');
        }

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousObjectifs = [...objectifs];
        setObjectifs(prev =>
            prev.map(obj =>
                obj.id_objectif === objectifId
                    ? { ...obj, current_value: parseFloat(currentValue), isOptimistic: true }
                    : obj
            )
        );

        try {
            const response = await fetch(`${API_BASE_URL}/objectifs/${objectifId}/progress`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ current_value: parseFloat(currentValue) })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }

            const data = await response.json();

            // Invalider le cache des objectifs
            invalidateCache('objectifs');
            console.log('🗑️ Cache objectifs invalidé après mise à jour progression');

            // Mettre à jour avec les vraies données
            setObjectifs(prev =>
                prev.map(obj =>
                    obj.id_objectif === objectifId 
                        ? { ...obj, ...data.objectif } 
                        : obj
                )
            );

            return { success: true, objectif: data.objectif };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setObjectifs(previousObjectifs);
            return handleError(err, 'Erreur lors de la mise à jour de la progression');
        }
    }, [objectifs, getHeaders, handleError, invalidateCache]);

    // 🔄 RAFRAÎCHIR LES OBJECTIFS (force le refetch)
    const refreshObjectifs = useCallback(() => {
        console.log('🔄 Force refresh des objectifs');
        return fetchObjectifs({}, false); // false = ne pas utiliser le cache
    }, [fetchObjectifs]);

    // 🎯 REFETCH avec gestion intelligente du cache
    const refetchObjectifs = useCallback(async () => {
        console.log('🔄 Refetch intelligent des objectifs');
        
        // Si les données sont récentes (moins de 1 minute), ne pas refetch
        if (lastFetch && (Date.now() - lastFetch) < 60000 && !forceRefresh) {
            console.log('⏭️ Données récentes, skip refetch');
            return { success: true, objectifs, fromCache: true };
        }
        
        return fetchObjectifs({}, false);
    }, [fetchObjectifs, lastFetch, forceRefresh, objectifs]);

    // 🔄 CHARGER LES OBJECTIFS AU MONTAGE
    useEffect(() => {
        if (autoRefresh) {
            fetchObjectifs();
        }
    }, [fetchObjectifs, autoRefresh]);

    // 🧹 NETTOYER LES ERREURS
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // États
        objectifs,
        loading,
        error,
        isFromCache,
        lastFetch,

        // Actions CRUD avec mise à jour optimiste
        createObjectif,
        updateObjectif,
        deleteObjectif,
        updateProgress,

        // Actions de rafraîchissement
        refresh: fetchObjectifs,
        refreshObjectifs,
        refetchObjectifs,

        // Utilitaires
        clearError,
        
        // Cache management
        invalidateCache: () => invalidateCache('objectifs')
    };
};